import { useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';

// Custom hook for managing all targeting state
const useTargetingState = (clearTargetingBanner) => {
  // Database targeting data state
  const [targetingData, setTargetingData] = useState(null);
  const [targetingDataLoaded, setTargetingDataLoaded] = useState(false);
  const [targetingDataError, setTargetingDataError] = useState(null);
  // Universal targeting state for deploy effects
  const [deployTargetMode, setDeployTargetMode] = useState(false);
  const [selectedDeployOrderId, setSelectedDeployOrderId] = useState(null);
  const [selectedDeployInstanceId, setSelectedDeployInstanceId] = useState(null);
  const [selectedDeployZone, setSelectedDeployZone] = useState(null);
  const [deployTargetInfo, setDeployTargetInfo] = useState(null);

  // Weapon targeting state
  const [weaponTargetMode, setWeaponTargetMode] = useState(false);
  const [selectedWeaponId, setSelectedWeaponId] = useState(null);
  const [selectedWeaponInstanceId, setSelectedWeaponInstanceId] = useState(null);
  
  // Weapon deploy targeting state
  const [weaponDeployTargetMode, setWeaponDeployTargetMode] = useState(false);
  const [weaponDeployTargetInfo, setWeaponDeployTargetInfo] = useState(null);

  // Drone targeting state
  const [droneTargetMode, setDroneTargetMode] = useState(false);
  const [selectedDroneId, setSelectedDroneId] = useState(null);
  const [selectedDroneInstanceId, setSelectedDroneInstanceId] = useState(null);

  // Immobilize targeting state
  const [immobilizeTargetMode, setImmobilizeTargetMode] = useState(false);
  const [selectedImmobilizeWeaponId, setSelectedImmobilizeWeaponId] = useState(null);

  // Confuse targeting state
  const [confuseTargetMode, setConfuseTargetMode] = useState(false);
  const [selectedConfuseWeaponId, setSelectedConfuseWeaponId] = useState(null);

  // Strategem targeting state
  const [strategemTargetMode, setStrategemTargetMode] = useState(false);
  const [selectedStrategemId, setSelectedStrategemId] = useState(null);
  const [strategemTargetInfo, setStrategemTargetInfo] = useState(null);

  // Special unit targeting state
  const [specialTargetMode, setSpecialTargetMode] = useState(false);
  const [selectedSpecialUnitId, setSelectedSpecialUnitId] = useState(null);

  // Replacement targeting state (for full zone unit replacement)
  const [replaceTargetMode, setReplaceTargetMode] = useState(false);
  const [selectedReplaceUnitId, setSelectedReplaceUnitId] = useState(null);
  const [selectedReplaceInstanceId, setSelectedReplaceInstanceId] = useState(null);
  const [replaceTargetZone, setReplaceTargetZone] = useState(null);
  const [clickedUnitForReplace, setClickedUnitForReplace] = useState(null);

  // Drone replacement targeting state (for full drone zone replacement)
  const [replaceDroneTargetMode, setReplaceDroneTargetMode] = useState(false);
  const [selectedReplaceDroneId, setSelectedReplaceDroneId] = useState(null);
  const [selectedReplaceDroneInstanceId, setSelectedReplaceDroneInstanceId] = useState(null);
  const [clickedDroneForReplace, setClickedDroneForReplace] = useState(null);

  // Load targeting data on mount
  useEffect(() => {
    const loadTargetingData = async () => {
      try {
        const response = await fetch(`${API_URL}/targeting-data`);
        if (!response.ok) {
          throw new Error('Failed to load targeting data');
        }
        const data = await response.json();
        setTargetingData(data);
        setTargetingDataLoaded(true);
      } catch (error) {
        console.error('Error loading targeting data:', error);
        setTargetingDataError(error.message);
        setTargetingDataLoaded(true);
      }
    };

    loadTargetingData();
  }, []);

  // Sync deploy targeting state with game state
  useEffect(() => {
    const syncDeployTargeting = (gameState, playerId) => {
      const backendDeployTargetMode = gameState?.state?.[playerId]?.deployTargetMode;
      const backendSelectedDeployOrderId = gameState?.state?.[playerId]?.selectedDeployOrderId;
      const backendSelectedDeployInstanceId = gameState?.state?.[playerId]?.selectedDeployInstanceId;
      const backendSelectedDeployZone = gameState?.state?.[playerId]?.selectedDeployZone;
      

  // ✅ CRITICAL FIX: Always clear frontend targeting when backend says targeting is done,
  // even if deployTargetMode is already false (e.g. optimistic clear after targetDeploy send).
  if (!backendDeployTargetMode) {
    if (
      deployTargetMode ||
      selectedDeployOrderId != null ||
      selectedDeployInstanceId != null ||
      deployTargetInfo
    ) {
      console.log('🎯 SYNC: Clearing deploy targeting state (backend said no)');
      setDeployTargetMode(false);
      setSelectedDeployOrderId(null);
      setSelectedDeployInstanceId(null);
      setSelectedDeployZone(null);
      setDeployTargetInfo(null);
      if (typeof clearTargetingBanner === 'function') {
        clearTargetingBanner();
      }
    }
    return;
  }

  // Backend says deploy targeting is active — sync authoritative instanceId / zone.
  if (
    backendSelectedDeployInstanceId &&
    backendSelectedDeployOrderId &&
    backendSelectedDeployZone
  ) {
    const zoneUnits = gameState?.state?.[playerId]?.playedOrders?.[backendSelectedDeployZone] || [];
    const unitExists = zoneUnits.some(u => u && u.instanceId === backendSelectedDeployInstanceId);

    if (unitExists) {
      if (
        !deployTargetMode ||
        selectedDeployOrderId !== backendSelectedDeployOrderId ||
        selectedDeployInstanceId !== backendSelectedDeployInstanceId ||
        selectedDeployZone !== backendSelectedDeployZone
      ) {
        console.log('🎯 SYNC: Activating deploy targeting state (backend said so)', {
          orderId: backendSelectedDeployOrderId,
          instanceId: backendSelectedDeployInstanceId,
          zone: backendSelectedDeployZone,
        });

        const unitObject = zoneUnits.find(u => u && u.instanceId === backendSelectedDeployInstanceId);

        setDeployTargetMode(true);
        setSelectedDeployOrderId(backendSelectedDeployOrderId);
        setSelectedDeployInstanceId(backendSelectedDeployInstanceId);
        setSelectedDeployZone(backendSelectedDeployZone);
        setDeployTargetInfo(unitObject ? { order: unitObject } : null);
      }
      return;
    }

    console.warn('⚠️ SYNC: Backend instanceId not found in zone', {
      backendInstanceId: backendSelectedDeployInstanceId,
      backendOrderId: backendSelectedDeployOrderId,
      backendZone: backendSelectedDeployZone,
      zoneUnits: zoneUnits.map(u => u ? { id: u.id, name: u.name, instanceId: u.instanceId } : null),
    });
  }

  if (
    !deployTargetMode ||
    selectedDeployOrderId !== backendSelectedDeployOrderId ||
    selectedDeployInstanceId !== backendSelectedDeployInstanceId
  ) {
    console.log('🎯 SYNC: Activating deploy targeting state (backend flags only)', {
      orderId: backendSelectedDeployOrderId,
      instanceId: backendSelectedDeployInstanceId,
      zone: backendSelectedDeployZone,
    });

    let unitObject = null;
    if (backendSelectedDeployInstanceId && backendSelectedDeployZone && gameState?.state?.[playerId]) {
      const zoneUnits = gameState.state[playerId].playedOrders?.[backendSelectedDeployZone] || [];
      unitObject = zoneUnits.find(u => u && u.instanceId === backendSelectedDeployInstanceId);
    }

    setDeployTargetMode(true);
    setSelectedDeployOrderId(backendSelectedDeployOrderId);
    setSelectedDeployInstanceId(backendSelectedDeployInstanceId);
    setSelectedDeployZone(backendSelectedDeployZone);
    setDeployTargetInfo(unitObject ? { order: unitObject } : null);
  }
    };

    window.syncDeployTargeting = syncDeployTargeting;

    return () => {
      delete window.syncDeployTargeting;
    };
  }, [deployTargetMode, selectedDeployOrderId, selectedDeployInstanceId, selectedDeployZone, deployTargetInfo, clearTargetingBanner]);

  // Sync strategem targeting state with game state
  useEffect(() => {
    const syncStrategemTargeting = (gameState, playerId) => {
      const needsTarget = gameState?.state?.[playerId]?.needsStrategemTarget;

      // Always clear frontend targeting when backend says targeting is done,
      // even if strategemTargetMode is already false (e.g. optimistic clear after targetStrategem send).
      if (!needsTarget) {
        if (strategemTargetMode || selectedStrategemId != null || strategemTargetInfo) {
          setStrategemTargetMode(false);
          setSelectedStrategemId(null);
          setStrategemTargetInfo(null);
          if (typeof clearTargetingBanner === 'function') {
            clearTargetingBanner();
          }
        }
        return;
      }

      const submittedInstanceId =
        typeof window.getStrategemTargetSubmittedInstanceId === 'function'
          ? window.getStrategemTargetSubmittedInstanceId()
          : null;
      if (
        submittedInstanceId &&
        needsTarget.strategemInstanceId &&
        String(submittedInstanceId) === String(needsTarget.strategemInstanceId)
      ) {
        if (strategemTargetMode || selectedStrategemId != null || strategemTargetInfo) {
          setStrategemTargetMode(false);
          setSelectedStrategemId(null);
          setStrategemTargetInfo(null);
          if (typeof clearTargetingBanner === 'function') {
            clearTargetingBanner();
          }
        }
        return;
      }

      console.log('🎯 SYNC: Setting strategem targeting state:', needsTarget);
      
      setStrategemTargetMode(true);
      setSelectedStrategemId(needsTarget.strategemId);
      setStrategemTargetInfo({
        strategemId: needsTarget.strategemId,
        strategemName: needsTarget.strategemName,
        strategemInstanceId: needsTarget.strategemInstanceId,
        type: needsTarget.type,
        targetingOptions: needsTarget.targetingOptions
      });
    };

    // This will be called from BattleUI when game state changes
    window.syncStrategemTargeting = syncStrategemTargeting;
  }, [strategemTargetMode, strategemTargetInfo, selectedStrategemId, clearTargetingBanner]);

  return {
    // Database targeting data
    targetingData,
    targetingDataLoaded,
    targetingDataError,
    
    // Deploy targeting state
    deployTargetMode,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    deployTargetInfo,
    
    // Weapon targeting state
    weaponTargetMode,
    selectedWeaponId,
    selectedWeaponInstanceId,
    
    // Weapon deploy targeting state
    weaponDeployTargetMode,
    weaponDeployTargetInfo,
    
    // Drone targeting state
    droneTargetMode,
    selectedDroneId,
    selectedDroneInstanceId,
    
    // Immobilize targeting state
    immobilizeTargetMode,
    selectedImmobilizeWeaponId,
    
    // Confuse targeting state
    confuseTargetMode,
    selectedConfuseWeaponId,
    
    // Strategem targeting state
    strategemTargetMode,
    selectedStrategemId,
    strategemTargetInfo,
    
    // Special unit targeting state
    specialTargetMode,
    selectedSpecialUnitId,
    
    // Replacement targeting state
    replaceTargetMode,
    selectedReplaceUnitId,
    selectedReplaceInstanceId,
    replaceTargetZone,
    clickedUnitForReplace,
    
    // Drone replacement targeting state
    replaceDroneTargetMode,
    selectedReplaceDroneId,
    selectedReplaceDroneInstanceId,
    clickedDroneForReplace,
    
    // Setters
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    setWeaponTargetMode,
    setSelectedWeaponId,
    setSelectedWeaponInstanceId,
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo,
    setDroneTargetMode,
    setSelectedDroneId,
    setSelectedDroneInstanceId,
    setImmobilizeTargetMode,
    setSelectedImmobilizeWeaponId,
    setConfuseTargetMode,
    setSelectedConfuseWeaponId,
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo,
    setSpecialTargetMode,
    setSelectedSpecialUnitId,
    setReplaceTargetMode,
    setSelectedReplaceUnitId,
    setSelectedReplaceInstanceId,
    setReplaceTargetZone,
    setClickedUnitForReplace,
    setReplaceDroneTargetMode,
    setSelectedReplaceDroneId,
    setSelectedReplaceDroneInstanceId,
    setClickedDroneForReplace
  };
};

export default useTargetingState;
